import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Order, PaymentMethod, Staff } from "@ai-pos/shared";
import { ordersApi, paymentMethodsApi, staffApi } from "../api/resources.js";

export function SalesHistory() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [businessDate, setBusinessDate] = useState("");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [paymentMethodId, setPaymentMethodId] = useState("");
  const [staffId, setStaffId] = useState("");
  const [includeVoided, setIncludeVoided] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    paymentMethodsApi.list().then((res) => setPaymentMethods(res.paymentMethods)).catch(() => undefined);
    staffApi
      .list()
      .then((res) => setStaffList(res.staff))
      .catch(() => undefined);
  }, []);

  const search = () => {
    ordersApi
      .list({
        businessDate: businessDate || undefined,
        receiptNumber: receiptNumber || undefined,
        paymentMethodId: paymentMethodId || undefined,
        staffId: staffId || undefined,
        includeVoided: includeVoided ? "true" : "false",
      })
      .then((res) => setOrders(res.orders))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "検索に失敗しました"));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(search, []);

  const totalAmount = useMemo(
    () => orders.filter((order) => order.status !== "VOIDED").reduce((sum, order) => sum + order.total, 0),
    [orders],
  );

  return (
    <div className="stack">
      <h2>売上履歴</h2>
      <div className="card row row-wrap">
        <div className="form-field">
          <label htmlFor="f-date">営業日</label>
          <input id="f-date" type="date" value={businessDate} onChange={(e) => setBusinessDate(e.target.value)} />
        </div>
        <div className="form-field">
          <label htmlFor="f-receipt">伝票番号</label>
          <input id="f-receipt" type="text" value={receiptNumber} onChange={(e) => setReceiptNumber(e.target.value)} />
        </div>
        <div className="form-field">
          <label htmlFor="f-payment">支払方法</label>
          <select id="f-payment" value={paymentMethodId} onChange={(e) => setPaymentMethodId(e.target.value)}>
            <option value="">すべて</option>
            {paymentMethods.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
        {staffList.length > 0 && (
          <div className="form-field">
            <label htmlFor="f-staff">スタッフ</label>
            <select id="f-staff" value={staffId} onChange={(e) => setStaffId(e.target.value)}>
              <option value="">すべて</option>
              {staffList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="form-field">
          <label htmlFor="f-voided">取消済みを含める</label>
          <input
            id="f-voided"
            type="checkbox"
            checked={includeVoided}
            onChange={(e) => setIncludeVoided(e.target.checked)}
            style={{ minHeight: "auto", width: 24, height: 24 }}
          />
        </div>
        <button className="btn btn-primary" onClick={search}>
          検索
        </button>
        <div className="row" style={{ gap: 6 }}>
          <span style={{ color: "var(--color-text-muted)", fontSize: 13 }}>合計</span>
          <span style={{ fontSize: 20, fontWeight: 700 }}>¥{totalAmount.toLocaleString("ja-JP")}</span>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>伝票番号</th>
              <th>日時</th>
              <th>スタッフ</th>
              <th>支払方法</th>
              <th>合計</th>
              <th>状態</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.id} onClick={() => navigate(`/sales/${order.id}`)} style={{ cursor: "pointer" }}>
                <td>{order.receiptNumber}</td>
                <td>{new Date(order.orderedAt).toLocaleString("ja-JP")}</td>
                <td>{order.staffName}</td>
                <td>{[...new Set(order.payments.map((p) => p.paymentMethodName))].join("、")}</td>
                <td>¥{order.total.toLocaleString("ja-JP")}</td>
                <td>
                  {order.status === "VOIDED" ? (
                    <span className="badge badge-danger">取消済み</span>
                  ) : (
                    <span className="badge">通常</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {orders.length === 0 && <div className="empty-state">該当する会計がありません</div>}
      </div>
    </div>
  );
}
